import { useEffect, useRef } from 'react';
import { Modal } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import {
  getAuthMethod,
  getTokenExpiresAt,
  removeAuthMethod,
  removeCurrentDistributor,
  removeToken,
  removeTokenExpiresAt,
  removeUserType,
  setToken,
  setTokenExpiresAt,
} from '@/utils/auth';

// How long before expiry to proactively renew the cached Auth0 ID token.
const AUTH0_REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

const TokenExpirationMonitor: React.FC = () => {
  const { isAuthenticated, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    let checkInterval: NodeJS.Timeout;
    let dialogShown = false;

    const showExpirationDialog = () => {
      if (dialogShown) return;
      dialogShown = true;

      const authMethod = getAuthMethod();
      Modal.warning({
        title: 'Login Expired',
        content: 'Your login has expired, please sign in again.',
        okText: 'Sign Back In',
        onOk: () => {
          removeToken();
          removeUserType();
          removeTokenExpiresAt();
          removeCurrentDistributor();
          removeAuthMethod();
          window.location.href =
            authMethod === 'auth0' ? '/auth/login' : '/admin/login?error=session_expired';
        },
        onCancel: () => {
          // Prevent closing by clicking mask or cancel
        },
        closable: false,
        maskClosable: false,
      });
    };

    // `useRefreshTokens` on Auth0Provider only keeps the SDK's own token
    // cache fresh. This app sends its own copy of the ID token (cached in
    // localStorage at login by Auth0Callback) with every API request, so
    // that copy must be renewed explicitly or it silently goes stale while
    // the SDK-side session remains valid.
    const refreshAuth0Token = async () => {
      if (isRefreshingRef.current) return;
      isRefreshingRef.current = true;
      try {
        await getAccessTokenSilently();
        const claims = await getIdTokenClaims();
        if (!claims) {
          showExpirationDialog();
          return;
        }
        setToken(claims.__raw);
        setTokenExpiresAt(
          claims.exp
            ? new Date(claims.exp * 1000).toISOString()
            : new Date(Date.now() + 3600 * 1000).toISOString(),
        );
      } catch (err) {
        // Refresh token is expired/revoked, fall back to a full re-login.
        showExpirationDialog();
      } finally {
        isRefreshingRef.current = false;
      }
    };

    const checkTokenExpiration = () => {
      const expiresAtStr = getTokenExpiresAt();
      if (!expiresAtStr) return;

      const expiresAt = new Date(expiresAtStr);
      const now = new Date();
      const msUntilExpiry = expiresAt.getTime() - now.getTime();

      if (getAuthMethod() === 'auth0') {
        if (isAuthenticated && msUntilExpiry <= AUTH0_REFRESH_BEFORE_EXPIRY_MS) {
          refreshAuth0Token();
        }
        return;
      }

      // Legacy (non-Auth0) sessions have no silent refresh path, so this is
      // the only warning the user gets: show the dialog once the token has
      // expired or will expire in the next 60 seconds.
      if (msUntilExpiry <= 60000) {
        showExpirationDialog();
        if (checkInterval) {
          clearInterval(checkInterval);
        }
      }
    };

    // Check every 10 seconds
    checkInterval = setInterval(checkTokenExpiration, 10000);

    // Check immediately on mount
    checkTokenExpiration();

    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [isAuthenticated, getAccessTokenSilently, getIdTokenClaims]);

  return null;
};

export default TokenExpirationMonitor;
